import { useState, useEffect } from "react";
import { Layout, PageHero } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/motion";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Briefcase, MapPin, GraduationCap, Heart, Zap, Globe, Coffee, X, Send, FileText, CheckCircle2, Building2 } from "lucide-react";
import { JOBS, INTERNSHIPS } from "../data";
import api from "../lib/api";
import { captureCrmLead } from "../lib/crm";

const BENEFITS = [
  { icon: Building2, t: "Physical Hubs & Hybrid", d: "Corporate HQ & regional innovation hubs paired with flexible hybrid work options." },
  { icon: Zap, t: "Extreme Ownership", d: "Architectural autonomy with zero bureaucracy. Ship production software from Day 1." },
  { icon: GraduationCap, t: "Mastery & Growth Fund", d: "Uncapped stipend for cloud certifications, global tech summits, books, and R&D." },
  { icon: Coffee, t: "Autonomous Velocity", d: "Work when you're sharpest. We measure execution impact and code elegance over hours." },
];

function JobCard({ job, onApply }) {
  return (
    <div className="card-soft rounded-2xl p-6 flex items-center justify-between gap-4 flex-wrap hover:border-[#FF5A1F]/30 transition-all duration-300">
      <div>
        <h3 className="font-heading text-lg font-semibold text-[#0B0B0B]">{job.title}</h3>
        <div className="flex items-center gap-4 text-sm text-neutral-500 mt-1 flex-wrap">
          <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-[#FF5A1F]" />{job.type}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#FF5A1F]" />{job.location}</span>
          <span className="px-2.5 py-0.5 rounded-full bg-black/5 text-neutral-600 text-xs font-medium">{job.team}</span>
        </div>
      </div>
      <button
        data-testid={`apply-${job.title}`}
        onClick={() => onApply(job)}
        className="px-6 py-2.5 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold hover:bg-[#ff6d38] transition-colors shadow-md shadow-[#FF5A1F]/20"
      >
        Apply
      </button>
    </div>
  );
}

export default function Careers() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobsList, setJobsList] = useState(JOBS);
  const [internshipsList, setInternshipsList] = useState(INTERNSHIPS);
  const [form, setForm] = useState({ name: "", email: "", phone: "", portfolioUrl: "", message: "" });
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeUrl, setResumeUrl] = useState("");
  const [uploadingResume, setUploadingResume] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/jobs")
      .then((res) => {
        if (res.data && res.data.length > 0) {
          const fulltime = res.data.filter((x) => x.category !== "internship" && x.type !== "Internship");
          const intern = res.data.filter((x) => x.category === "internship" || x.type === "Internship");
          if (fulltime.length > 0) setJobsList(fulltime);
          if (intern.length > 0) setInternshipsList(intern);
        }
      })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));


  const openModal = (job) => {
    setSelectedJob(job);
    setForm({ name: "", email: "", phone: "", portfolioUrl: "", message: "" });
    setResumeFile(null);
    setResumeUrl("");
    setSubmitted(false);
  };

  const closeModal = () => {
    setSelectedJob(null);
    setSubmitted(false);
  };

  const handleResumeChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResumeFile(file);
    setUploadingResume(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/upload/resume", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data?.url) {
        setResumeUrl(res.data.url);
        toast.success("Resume uploaded successfully!");
      }
    } catch (err) {
      toast.error("Failed to upload resume. You can still paste your LinkedIn / Portfolio URL.");
    } finally {
      setUploadingResume(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      const roleTitle = selectedJob?.title || "General Application";

      // Direct CRM webhook capture
      captureCrmLead({
        name: form.name,
        email: form.email,
        phone: form.phone || "",
        service: `Careers: ${roleTitle}`,
        role: roleTitle,
        portfolio_url: form.portfolioUrl || "",
        resume_url: resumeUrl || "",
        notes: form.message || `Application for ${roleTitle}`,
      });

      await api.post("/applications", {
        name: form.name,
        email: form.email,
        phone: form.phone || "",
        role: roleTitle,
        portfolio_url: form.portfolioUrl || "",
        resume_url: resumeUrl || "",
        message: form.message || `Application for ${roleTitle}`
      });
      setSubmitted(true);
      toast.success("Application received successfully!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const inputCls = "w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F] transition-colors placeholder:text-neutral-400";

  return (
    <Layout>
      <Seo title="Careers - Join Our Team" description="Join Neuravolt (VOLTNOVATE SOLUTIONS LLP). Explore open roles and internships in software engineering, AI development, design, and growth. Work on cutting-edge technology from Gwalior, India." keywords="Neuravolt careers, software engineer jobs India, AI developer jobs, tech internships Gwalior, VOLTNOVATE SOLUTIONS LLP jobs" path="/careers" />
      <PageHero eyebrow="Careers" title="Build the future with us" sub="We're a high-performing engineering team with physical corporate hubs and hybrid flexibility, obsessed with craft, velocity and outcomes." />

      <section className="py-20 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Benefits Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.t} delay={(i % 4) * 0.07}>
                  <div className="rounded-3xl p-7 border border-black/5 bg-[#FAF9F6] h-full">
                    <span className="w-11 h-11 rounded-xl bg-[#FF5A1F]/10 grid place-items-center mb-4">
                      <Icon className="w-5 h-5 text-[#FF5A1F]" />
                    </span>
                    <h3 className="font-heading font-semibold text-[#0B0B0B] mb-1">{b.t}</h3>
                    <p className="text-neutral-500 text-sm">{b.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Open Full-time Positions */}
          <div className="mb-16">
            <h2 className="font-display text-3xl font-bold text-[#0B0B0B] mb-6">Open positions</h2>
            <div className="space-y-3">
              {jobsList.map((j) => (
                <JobCard key={j.id || j.title} job={j} onApply={openModal} />
              ))}
            </div>
          </div>

          {/* Internships Section */}
          <div className="mb-20">
            <h2 className="font-display text-3xl font-bold text-[#0B0B0B] mb-2 flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-[#FF5A1F]" /> Internships
            </h2>
            <p className="text-neutral-500 mb-6">Kickstart your career with hands-on, mentored projects.</p>
            <div className="space-y-3">
              {internshipsList.map((j) => (
                <JobCard key={j.id || j.title} job={j} onApply={openModal} />
              ))}
            </div>
          </div>


          {/* Spontaneous Application Banner (MNC Standard) */}
          <Reveal>
            <div className="rounded-3xl bg-[#0B0B0B] p-10 sm:p-12 text-white relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="max-w-xl relative z-10">
                <span className="text-[#FF5A1F] text-xs font-semibold uppercase tracking-widest font-heading mb-2 block">
                  Don't see your role?
                </span>
                <h3 className="font-display text-2xl sm:text-3xl font-bold mb-3">We are always looking for top talent</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  If you're passionate about AI engineering, high-performance web systems, or mobile architecture, send us a general application or email us directly at <span className="text-[#FF5A1F] font-semibold">careers@neuravolt.in</span>.
                </p>
              </div>
              <button
                onClick={() => openModal({ title: "General Application", team: "All Teams", location: "Remote", type: "Full-Time / Internship" })}
                className="shrink-0 px-8 py-4 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors shadow-lg shadow-[#FF5A1F]/30 relative z-10"
              >
                Submit General Application
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Professional Application Modal */}
      <AnimatePresence>
        {selectedJob && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Modal Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.4 }}
              className="relative w-full max-w-xl bg-white rounded-3xl p-6 sm:p-8 shadow-2xl z-10 border border-black/10 my-auto"
            >
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-5 right-5 w-9 h-9 rounded-full bg-neutral-100 grid place-items-center text-neutral-500 hover:text-black hover:bg-neutral-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {submitted ? (
                <div className="text-center py-8 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-[#0B0B0B]">Application Received!</h3>
                  <p className="text-neutral-500 text-sm max-w-sm mx-auto">
                    Thank you for applying for <span className="font-semibold text-black">{selectedJob.title}</span>. Our talent team will review your profile and reach out within 3 to 5 business days.
                  </p>
                  <button
                    onClick={closeModal}
                    className="mt-6 px-7 py-3 rounded-full bg-[#0B0B0B] text-white font-semibold text-sm hover:bg-neutral-800 transition-colors"
                  >
                    Close Window
                  </button>
                </div>
              ) : (
                <div>
                  <div className="mb-6 border-b border-black/5 pb-4">
                    <span className="text-xs font-bold text-[#FF5A1F] uppercase tracking-wider block mb-1">
                      Job Application
                    </span>
                    <h3 className="font-display text-2xl font-bold text-[#0B0B0B]">{selectedJob.title}</h3>
                    <div className="flex items-center gap-3 text-xs text-neutral-500 mt-2">
                      <span className="px-2.5 py-1 rounded-md bg-neutral-100 text-neutral-700 font-medium">{selectedJob.team}</span>
                      <span>•</span>
                      <span>{selectedJob.type}</span>
                      <span>•</span>
                      <span>{selectedJob.location}</span>
                    </div>
                  </div>

                  <form onSubmit={submit} data-testid="careers-form" className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">Full Name *</label>
                        <input
                          data-testid="career-name"
                          className={inputCls}
                          placeholder="Jane Doe"
                          value={form.name}
                          onChange={(e) => set("name", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">Email Address *</label>
                        <input
                          data-testid="career-email"
                          type="email"
                          className={inputCls}
                          placeholder="jane@example.com"
                          value={form.email}
                          onChange={(e) => set("email", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">Phone Number</label>
                        <input
                          data-testid="career-phone"
                          className={inputCls}
                          placeholder="+1 (555) 000-0000"
                          value={form.phone}
                          onChange={(e) => set("phone", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">LinkedIn / Portfolio URL</label>
                        <input
                          data-testid="career-role"
                          className={inputCls}
                          placeholder="https://linkedin.com/in/..."
                          value={form.portfolioUrl}
                          onChange={(e) => set("portfolioUrl", e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Resume Upload Dropzone */}
                    <div>
                      <label className="block text-xs font-semibold text-neutral-700 mb-1">Upload Resume (PDF, DOC, DOCX)</label>
                      {resumeUrl ? (
                        <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs">
                          <span className="flex items-center gap-2 font-medium text-emerald-800 truncate">
                            <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                            {resumeFile?.name || "Resume Uploaded"}
                          </span>
                          <button
                            type="button"
                            onClick={() => { setResumeFile(null); setResumeUrl(""); }}
                            className="text-xs text-rose-600 hover:underline ml-2 shrink-0 font-semibold"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="relative border-2 border-dashed border-black/15 rounded-xl p-4 text-center hover:border-[#FF5A1F] transition-colors bg-[#FAF9F6]">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleResumeChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <div className="flex flex-col items-center justify-center gap-1">
                            <FileText className="w-6 h-6 text-[#FF5A1F]" />
                            <span className="text-xs font-semibold text-neutral-800">
                              {uploadingResume ? "Uploading Resume..." : "Click or drag to upload Resume (PDF/DOC)"}
                            </span>
                            <span className="text-[10px] text-neutral-400">Max size 10MB</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-neutral-700 mb-1">Cover Note / Why Neuravolt?</label>
                      <textarea
                        data-testid="career-message"
                        rows={2}
                        className={inputCls + " resize-none"}
                        placeholder="Tell us briefly about your experience, projects, or background..."
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                      />
                    </div>

                    <div className="pt-2">
                      <button
                        data-testid="career-submit"
                        type="submit"
                        disabled={loading || uploadingResume}
                        className="w-full px-7 py-3.5 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-[#FF5A1F]/25"
                      >
                        {loading ? "Submitting..." : <>Submit Application <Send className="w-4 h-4" /></>}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </Layout>
  );
}


